import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, ViewEncapsulation } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { EnpsControlComponent } from '@src/suite/common/controls/enps-control/enps-control';

interface TreeNode {
    value: string;
    id: string;
    parentId?: string;
    children?: TreeNode[];
}

interface FlatTreeNode {
    value: string;
    id: string;
    parentId?: string;
    level: number;
    expandable: boolean;
}

@Component({
    selector: 'st-tree-checklist',
    templateUrl: './tree-checklist.html',
    styleUrls: ['./tree-checklist.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'st-tree-checklist',
    },
})
export class TreeChecklistComponent extends EnpsControlComponent<Array<string>> {
    private _transformer = (node: TreeNode, level: number) => ({
        expandable: !!node.children && node.children.length > 0,
        value: node.value,
        id: node.id,
        parentId: node.parentId,
        level,
    });

    treeControl = new FlatTreeControl<FlatTreeNode>(
        node => node.level,
        node => node.expandable
    );

    treeFlattener = new MatTreeFlattener(
        this._transformer,
        node => node.level,
        node => node.expandable,
        node => node.children
    );
    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    checklistSelection = new SelectionModel<string>(true /* multiple */);
    hasChild = (_: number, node: FlatTreeNode) => node.expandable;
    getLevel = (node: FlatTreeNode) => node.level;
    itemList = input<TreeNode[]>([]);

    itemListFlat = signal<{ [id: string]: string }>({});

    selectionChange = output<any>();
    panelOpen = signal(false);

    internalValue = signal<Array<string>>([]);

    constructor() {
        super();
        effect(
            () => {
                this.populateItemListFlat(this.itemList());
                this.checklistSelection.setSelection(...(this.value() || []));
                this.internalValue.set([...(this.value() || [])]);
            },
            { allowSignalWrites: true }
        );
    }

    _canOpen = computed(() => {
        return !this.panelOpen() && !this.disabled();
    });

    toggle(): void {
        if (this.panelOpen()) {
            this.close();
            return;
        }
        this.dataSource.data = this.itemList();
        this.open();
    }

    private populateItemListFlat(list: TreeNode[]) {
        list.forEach(item => {
            this.itemListFlat.update(val => {
                val[item.id] = item.value;
                if (item.children && Array.isArray(item.children) && item.children.length > 0) {
                    this.populateItemListFlat(item.children);
                }
                return val;
            });
        });
    }

    /** Opens the overlay panel. */
    open(): void {
        if (this._canOpen()) {
            this.panelOpen.set(true);
            const { internalValue } = this;
            this.checklistSelection.setSelection(...internalValue());
        }
    }

    /** Closes the overlay panel and focuses the host element. */
    close(): void {
        if (this.panelOpen()) {
            this.panelOpen.set(false);
            this.changed.emit(this.internalValue());
        }
    }

    public getItemLabel(item: any) {
        return this.itemListFlat()[item];
    }

    hasChildren(_: number, node) {
        return node.Children.length > 0;
    }

    descendantsAllSelected(node: FlatTreeNode): boolean {
        const descendants = this.treeControl.getDescendants(node);
        const descAllSelected = descendants.length > 0 && descendants.every(child => this.checklistSelection.isSelected(child.id));
        return descAllSelected;
    }

    /** Whether part of the descendants are selected */
    descendantsPartiallySelected(node: FlatTreeNode): boolean {
        const descendants = this.treeControl.getDescendants(node);
        const result = descendants.some(child => this.checklistSelection.isSelected(child.id));
        return result && !this.descendantsAllSelected(node);
    }

    /** Toggle the to-do item selection. Select/deselect all the descendants node */
    itemSelectionToggle(node: FlatTreeNode): void {
        this.checklistSelection.toggle(node.id);
        const descendants = this.treeControl.getDescendants(node).map(n => n.id);
        this.checklistSelection.isSelected(node.id) ? this.checklistSelection.select(...descendants) : this.checklistSelection.deselect(...descendants);

        // Force update for the parent
        descendants.forEach(child => this.checklistSelection.isSelected(child));
        this.checkAllParentsSelection(node);
        const value = this.checklistSelection.selected;
        this.selectionChange.emit({ id: node.id, state: value });
    }

    /** Toggle a leaf to-do item selection. Check all the parents to see if they changed */
    leafItemSelectionToggle(node: FlatTreeNode): void {
        this.checklistSelection.toggle(node.id);
        this.checkAllParentsSelection(node);
        const value = this.checklistSelection.selected;
        this.selectionChange.emit({ id: node.id, state: value });
    }

    /** Checks all the parents when a leaf node is selected/unselected */
    checkAllParentsSelection(node: FlatTreeNode): void {
        let parent: FlatTreeNode | null = this.getParentNode(node);
        while (parent) {
            this.checkRootNodeSelection(parent);
            parent = this.getParentNode(parent);
        }
    }

    /** Remove item from selected values */
    remove(id: string) {
        this.internalValue.set(this.internalValue().filter(i => i !== id));
        this.selectionChange.emit({ id: id, state: this.internalValue() });
    }

    /** Check root node checked state and change it accordingly */
    checkRootNodeSelection(node: FlatTreeNode): void {
        const nodeSelected = this.checklistSelection.isSelected(node.id);
        const descendants = this.treeControl.getDescendants(node);
        const descAllSelected = descendants.length > 0 && descendants.every(child => this.checklistSelection.isSelected(child.id));
        if (nodeSelected && !descAllSelected) {
            this.checklistSelection.deselect(node.id);
        } else if (!nodeSelected && descAllSelected) {
            this.checklistSelection.select(node.id);
        }
    }

    /* Get the parent node of a node */
    getParentNode(node: FlatTreeNode): FlatTreeNode | null {
        const currentLevel = this.getLevel(node);

        if (currentLevel < 1) {
            return null;
        }

        const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

        for (let i = startIndex; i >= 0; i--) {
            const currentNode = this.treeControl.dataNodes[i];

            if (this.getLevel(currentNode) < currentLevel) {
                return currentNode;
            }
        }
        return null;
    }
}
